import { Button, Modal, Row, Tag, Typography } from "antd";
import React, { useRef, useState } from "react";
import { toJpeg } from "html-to-image";
import { saveAs } from "file-saver";
import { GetOrderDetailData } from "@/services/interface";
import { numberFormat } from "utils/nuberFormat";
import { isCanceledItemStatus } from "utils/orderStatus";
import { getOrderSummary } from "utils/orderSummary";

interface ModalPrintPurchaseOrderProps {
  item: GetOrderDetailData;
  /** ยอดชำระล่าสุดจาก webhook (ส่งมาจาก ModalOrderDetail) — ถ้าไม่ส่งมาใช้ item.paid_amt */
  paidAmtOverride?: number;
  /** สถานะการชำระเงินล่าสุดจาก webhook (ส่งมาจาก ModalOrderDetail) */
  paymentStatusOverride?: string;
}

const ModalPrintPurchaseOrder = ({
  item,
  paidAmtOverride,
  paymentStatusOverride,
}: ModalPrintPurchaseOrderProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const receiptRef = useRef<HTMLDivElement>(null);

  const showModal = () => {
    setIsModalOpen(true);
  };

  const handleCancel = () => {
    setIsModalOpen(false);
  };

  const saveReceiptAsImage = () => {
    if (receiptRef.current) {
      toJpeg(receiptRef.current) // เปลี่ยนการใช้งาน htmlToImage เป็น toJpeg
        .then(function (dataUrl) {
          saveAs(
            dataUrl,
            `receipt_${item?.items[0]?.order_code}_${item.address_name}.jpg`
          );
        });
    }
  };

  /** สรุปยอดเงิน — ใช้ helper ตัวเดียวกับ ModalOrderDetail และใบส่งของ ให้ตัวเลขตรงกันทุกจอ */
  const {
    totalPrice,
    totalOrderPrice,
    totalTransportAmount,
    totalAmountPrice,
    displayPaymentAmount,
    remainingAmount,
    paymentStatus,
    showVipDiscount,
    showFactoryDiscount,
  } = getOrderSummary(item, {
    paid_amt: paidAmtOverride,
    payment_status: paymentStatusOverride,
  });

  return (
    <>
      <Button style={{ marginRight: 5 }} type="default" onClick={showModal}>
        ดาวน์โหลดรูปใบสั่งซื้อ
      </Button>
      <Modal closeIcon={false} open={isModalOpen} footer={false}>
        <div>
          <div
            style={{ backgroundColor: "white", padding: 10 }}
            ref={receiptRef}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <div style={{ flex: 1 }}></div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Typography.Text>คำสั่งซื้อของ {item.ref_name}</Typography.Text>
              </div>
            </div>
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "center",
                alignItems: "center",
                marginTop: 10,
              }}
            >
              <div style={{ flex: 1 }}>
                <Typography.Text
                  style={{ fontSize: 14, fontWeight: 500, color: "#1DB446" }}
                >
                  สรุปคำสั่งซื้อ
                </Typography.Text>
              </div>
              <div style={{ display: "flex", justifyContent: "flex-end" }}>
                <Typography.Text>{item?.items[0]?.order_code}</Typography.Text>
              </div>
            </div>
            <div style={{ borderBottom: "1px solid #f0f0f0" }}></div>
            <div style={{ marginTop: 10 }}>
              {item?.items.map((i, index) => (
                <div key={i.price_id}>
                  <Row>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        width: "100%",
                      }}
                    >
                      {/* <div style={{ flex: 1 }}>
                         <img
                          src={i.image_url}
                          alt={i.product_name}
                          style={{
                            width: 40,
                            height: 40,
                            marginRight: 10,
                          }}
                        /> 
                      </div> */}
                      <div style={{ flex: 3, marginLeft: 10 }}>
                        <Typography.Paragraph style={{ marginBottom: 0 }}>
                          {i.product_name}
                        </Typography.Paragraph>
                        <Typography.Paragraph style={{ fontSize: "12px" }}>
                          {i.sku_description}
                        </Typography.Paragraph>
                        {isCanceledItemStatus(i.item_status) && (
                          <Tag color="error" style={{ marginInlineEnd: 0 }}>
                            ยกเลิก
                          </Tag>
                        )}
                      </div>
                      <div
                        style={{
                          flex: 1,
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "end",
                        }}
                      >
                        {item.order_is_vip ? (
                          <>
                            <Typography.Paragraph
                              style={{ marginBottom: 0 }}
                            >
                              {numberFormat(
                                i.wholesale_price
                              )}{" "}
                              บาท
                            </Typography.Paragraph>
                            <Typography.Text
                              style={{
                                fontSize: "12px",
                                whiteSpace: "nowrap",
                              }}
                              type="warning"
                            >
                              ราคาสิทธิพิเศษ {numberFormat(i.vip_price)}{" "}
                              บาท
                            </Typography.Text>
                          </>
                        ) : (
                          <Typography.Paragraph
                            style={{ marginBottom: 0 }}
                          >
                            {item.ref_source === 'retail' ? numberFormat(i.wholesale_price) : numberFormat(i.sku_price)}{" "}
                            บาท
                          </Typography.Paragraph>
                        )}
                        <Typography.Paragraph style={{ marginBottom: 0 }}>
                          จำนวน {i.sku_cnt}
                        </Typography.Paragraph>
                        {/* <Typography.Paragraph style={{ marginBottom: 0 }}>
                                ราคา {numberFormat(i.sku_price* i.sku_cnt)} 
                              </Typography.Paragraph> */}
                      </div>
                    </div>
                  </Row>
                  {i.contact_comment_item && (
                    <Typography.Paragraph
                      style={{ fontSize: "12px", marginTop: 0, marginLeft: 10, whiteSpace: 'pre-line' }}
                    >
                      <span style={{ fontWeight: 500 }}>โน๊ตผู้ประกอบการ:</span>{" "}
                      &nbsp;
                      {i.contact_comment_item}
                    </Typography.Paragraph>
                  )}
                </div>
              ))}
            </div>
            <div style={{ borderTop: "1px solid #f0f0f0", marginRight: 0 }}>
              <div
                style={{
                  display: "flex",
                  marginRight: 0,
                  marginTop: 10,
                }}
              >
                <div style={{ flex: 1 }}>
                  <Typography.Text style={{}}>ยอดรวมสินค้า</Typography.Text>
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "end",
                  }}
                >
                  {item && (
                    <Typography.Text>
                      {numberFormat(totalOrderPrice)}{" "}
                      บาท
                    </Typography.Text>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  marginRight: 0,
                  marginTop: 10,
                }}
              >
                <div style={{ flex: 1 }}>
                  <Typography.Text style={{}}>ค่าจัดส่ง</Typography.Text>
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "end",
                  }}
                >
                  <Typography.Text>
                    {numberFormat(totalTransportAmount)} บาท
                  </Typography.Text>
                </div>
              </div>
              {showVipDiscount ? (
                // <div
                //   style={{
                //     display: "flex",
                //     marginRight: 0,
                //     marginTop: 10,
                //   }}
                // >
                //   <div style={{ flex: 1 }}>
                //     <Typography.Text style={{ color: "orange" }}>
                //       ส่วนลดสิทธิพิเศษ
                //     </Typography.Text>
                //   </div>
                //   <div
                //     style={{
                //       flex: 1,
                //       display: "flex",
                //       flexDirection: "column",
                //       alignItems: "end",
                //     }}
                //   >
                //     <Typography.Text style={{ color: "orange" }}>
                //       {numberFormat(
                //         item.extra_discount_amount
                //       )}{" "} บาท
                //     </Typography.Text>
                //   </div>
                // </div>
                <></>
              ) : null}

              {showFactoryDiscount ? (
                <div
                  style={{
                    display: "flex",
                    marginRight: 0,
                    marginTop: 10,
                  }}
                >
                  <div style={{ flex: 1 }}>
                    <Typography.Text style={{}}>ส่วนลดจากโรงงาน</Typography.Text>
                  </div>
                  <div
                    style={{
                      flex: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "end",
                    }}
                  >
                    <Typography.Text>
                      {numberFormat((item?.discount_amount || 0))} บาท
                    </Typography.Text>
                  </div>
                </div>
              ) : null}
            </div>
            <div style={{ borderTop: "1px solid #f0f0f0", marginRight: 0 }}>
              <div
                style={{
                  display: "flex",
                  marginTop: 10,
                }}
              >
                <div style={{ flex: 1 }}>
                  <Typography.Text style={{ fontWeight: "500" }}>
                    ยอดรวมทั้งหมด
                  </Typography.Text>
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "end",
                  }}
                >
                  {item && (
                    <Typography.Text style={{ fontWeight: "500" }}>
                      {numberFormat(totalAmountPrice)} บาท
                    </Typography.Text>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  marginTop: 10,
                }}
              >
                <div style={{ flex: 1 }}>
                  <Typography.Text>ยอดชำระเงินรวม</Typography.Text>
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "end",
                  }}
                >
                  {item && (
                    <Typography.Text>
                      {numberFormat(displayPaymentAmount)} บาท
                    </Typography.Text>
                  )}
                </div>
              </div>
              <div
                style={{
                  display: "flex",
                  marginTop: 10,
                }}
              >
                <div style={{ flex: 1, display: "flex", alignItems: "center" }}>
                  <Typography.Text style={{ fontWeight: 500 }}>
                    ยอดเงินคงเหลือที่ต้องชำระ
                  </Typography.Text>
                </div>
                <div
                  style={{
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "end",
                  }}
                >
                  {item && Number(remainingAmount) > 0 && (
                    <Typography.Text
                      style={{ fontWeight: 500, color: "red" }}
                    >
                      {/* COD ใช้ remainingAmount เสมอเหมือน manual_transfer (คำนวณจาก paid_amt
                          แล้ว ไม่ใช่ bank_payment) — payment type อื่นที่ยังไม่ผูก paid_amt
                          ค่อย fallback ไป totalPrice - pay_point ตามเดิม */}
                      {item.order_payment_type === "manual_transfer" ||
                      item.order_payment_type === "COD"
                        ? numberFormat(Number(remainingAmount))
                        : paymentStatus === "partial_payment"
                          ? numberFormat(Number(remainingAmount))
                          : numberFormat(
                            Number(totalPrice - item.pay_point)
                          )}{" "}
                      บาท
                    </Typography.Text>
                  )}
                  {/* 30/9/2026 — บรรทัด "ชำระเงินเกินมา n บาท" (แดง) เหลือเฉพาะออเดอร์เงินโอน
                      (manual_transfer) ที่โรงงานต้องคืนเงินส่วนเกินให้ลูกค้าเอง · ประเภทอื่นวาดเหมือน
                      จ่ายครบและไม่บอกส่วนต่าง เพราะเงินส่วนเกินไม่ใช่งานของโรงงาน แต่ประโยคสีแดง
                      อ่านว่ามีอะไรต้องจัดการ · เงื่อนไขยังแยกไว้ไม่รวมกับ === 0 ข้างล่าง เพราะบล็อกนั้น
                      พ่วง payment_status === "confirm" อยู่ ถ้ารวมดื้อ ๆ ออเดอร์จ่ายเกินที่สถานะยังเป็น
                      pending จะไม่วาดอะไรเลย */}
                  {item && Number(remainingAmount) < 0 &&
                    (item.order_payment_type === "manual_transfer" ? (
                      <Typography.Text style={{ fontWeight: 500, color: "red" }}>
                        ชำระเงินเกินมา {numberFormat(-Number(remainingAmount))} บาท
                      </Typography.Text>
                    ) : (
                      <Typography.Text style={{ fontWeight: 500, color: "green" }}>
                        ชำระเงินครบถ้วนแล้ว
                      </Typography.Text>
                    ))}
                  {item &&
                    Number(remainingAmount) === 0 &&
                    paymentStatus === "confirm" && (
                      <Typography.Text
                        style={{ fontWeight: 500, color: "green" }}
                      >
                        ชำระเงินครบถ้วนแล้ว
                      </Typography.Text>
                    )}

                  {item &&
                    Number(remainingAmount) === 0 &&
                    paymentStatus === "pending" && (
                      <Typography.Text
                        style={{ fontWeight: 500, color: "red" }}
                      >
                        {numberFormat(totalPrice - item.pay_point)} บาท
                      </Typography.Text>
                    )}
                </div>
              </div>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <Button style={{ marginRight: 10 }} onClick={handleCancel}>
              ปิด
            </Button>
            <Button type="primary" onClick={saveReceiptAsImage}>
              บันทึกรูปภาพ
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default ModalPrintPurchaseOrder;
